import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";

import { Container, Row, Col, Form, Spinner, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import PropertyOpenWorkOrders from "./PropertyOpenWorkOrders";
import PropertyClosedWorkOrders from "./PropertyClosedWorkOrders";
import useLogger from "../../util/Logger";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import { useProperty } from "../../data/PropertyHook";
import "./Property.css";
import { isWorkOrderClosed, isWorkOrderOpen } from "../../data/WorkOrderStatus";
import AccessNotes from "../shared/AccessNotes";

function Property() {
  const navigate = useNavigate();
  const logger = useLogger();
  const breadcrumbRepository = useBreadcrumb();
  const workOrdersRepository = useWorkOrders();
  const propertyRepository = useProperty();

  const [property, setProperty] = useState(null);
  const [workOrders, setWorkOrders] = useState(null);

  const { propertyId } = useParams();

  useEffect(() => {
    const getData = async () => {
      try {
        logger.debug("Property.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
        ]);

        logger.debug(
          "Property.useEffect",
          `Retrieving property '${propertyId}'`
        );

        const [retrievedProperty, retrievedWorkOrders] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          workOrdersRepository.getWorkOrders(propertyId),
        ]);

        setProperty(retrievedProperty);
        setWorkOrders(retrievedWorkOrders);
      } catch (error) {
        logger.error(
          "Property.useEffect",
          `An error occurred while loading property '${propertyId}' ${error}`
        );
        toast.error(
          "An error occurred while loading the property. Please try again."
        );
        navigate("/");
      }
    };
    getData();

    // NOTE:
    //  Think very carefully before adding dependencies to this array. If we get in an infinite loop
    //  we'll hammer the API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const view =
    !property || !workOrders ? (
      <Container className="coreContent">
        <Spinner
          animation="border"
          role="status"
          style={{
            marginTop: "200px",
            marginLeft: "50%",
            marginBottom: "200px",
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    ) : (
      <Container className="coreContent">
        <h2>Property</h2>
        <Row className="mb-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Property Id:</Form.Label>
          </Col>
          <Col xs={4}>
            <Form.Label>{property.id}</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Address</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.fullAddress} />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Postcode</Form.Label>
          </Col>
          <Col xs={2}>
            <Form.Control type="text" readOnly value={property.postCode} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Site</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.siteName} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Access Notes</Form.Label>
          </Col>
          <Col xs={6}>
            <AccessNotes accessNotes={property.accessNotes} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">
              Property Access Notes
            </Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control
              as="textarea"
              readOnly
              value={property.propertyAccessNotes}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <PropertyOpenWorkOrders
              openWorkOrders={workOrders.filter((workOrder) =>
                isWorkOrderOpen(workOrder)
              )}
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Link to={`/property/${property.id}/workorder/create`}>
            <Button>New Reactive Work Order</Button>
          </Link>
        </Row>
        <Row>
          <Col>
            <PropertyClosedWorkOrders
              closedWorkOrders={workOrders.filter((workOrder) =>
                isWorkOrderClosed(workOrder)
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4 mb-4">
          <Col>
            <Link to="/">
              <Button variant="secondary">Back to search</Button>
            </Link>
          </Col>
        </Row>
      </Container>
    );

  return view;
}

export default Property;
